import { useAuth } from "../../../context/AuthContext";
import { useEffect, useState } from "react";
import { InverntroyService } from "../../../services/api/inventroy.service";
import avatarPlaceholder from "../../../assets/images/default_avatar.jpg";
import toast from "react-hot-toast";
import { DataTable } from "../../DataTable/DataTable";
import CreateRequestModal from "./CreateRequestModal";
import { Eye } from "lucide-react";
import ViewRequestedProductsModal from "./ViewRequestedProductsModal";

interface RequestedStockInterface {
  id: string;
  clinicId: string;
  clinicName: string;
  clinicImage: string;
  clinicNumber: string;
  requestDate: string;
  requestStatus: string;
  products: [
    {
      id: string;
      productName: string;
      quantity: string;
    },
  ];
}
const RequestedStock = ({ clinicId }: { clinicId: string }) => {
  const [requestedStock, setRequestedStock] = useState<
    RequestedStockInterface[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    pageSize: 10,
    totalItemsCount: 0,
  });

  const fetchRequestedStock = async () => {
    try {
      setLoading(true);
      const response = await InverntroyService.getStockRequestListing(
        pagination.page,
        pagination.pageSize,
        clinicId,
      );
      if (response.success) {
        setRequestedStock(response.data?.data);
      } else {
        toast.error(response.message);
        setRequestedStock([]);
      }
      setPagination({
        page: response.data?.pagination.page,
        totalPages: response.data?.pagination.totalPages,
        pageSize: response.data?.pagination.pageSize,
        totalItemsCount: response.data?.pagination.totalItemsCount,
      });
    } catch (error) {
      console.log(error);
      toast.error("Error while fetching the requested stock");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = () => {
    setIsRequestModalOpen(false);
    fetchRequestedStock();
  };

  const handleViewProducts = (products: any[]) => {
    setSelectedProducts(products);
    setIsViewModalOpen(true);
  };

  useEffect(() => {
    fetchRequestedStock();
  }, [pagination.page]);

  const handlePageChange = (page: number) => {
    setPagination({
      ...pagination,
      page,
    });
  };
  const handlePageSizeChange = (pageSize: number) => {
    setPagination({
      ...pagination,
      pageSize,
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  };

  const columns = [
    {
      key: "Clinic Name",
      title: "Clinic Name",
      sortable: true,
      width: "w-[250px]",
      render: (data: RequestedStockInterface) => (
        <div className="flex items-center">
          <img
            src={data.clinicImage || avatarPlaceholder}
            alt={data.clinicName}
            className="w-8 h-8 rounded-full mr-3 object-cover border border-gray-200"
          />
          <div>
            <div className="font-medium text-gray-900">{data.clinicName}</div>
          </div>
        </div>
      ),
    },
    {
      key: "Clinic Number",
      title: "Clinic Number",
      sortable: true,
      render: (data: RequestedStockInterface) => (
        <>
          <div>{data?.clinicNumber || "N/A"}</div>
        </>
      ),
    },
    {
      key: "Request Date",
      title: "Request Date",
      sortable: true,
      render: (data: RequestedStockInterface) => (
        <>
          <div>{formatDate(data?.requestDate) || "N/A"}</div>
        </>
      ),
    },
    {
      key: "Request Status",
      title: "Request Status",
      sortable: true,
      render: (data: RequestedStockInterface) => (
        <>
          <div>{data?.requestStatus || "N/A"}</div>
        </>
      ),
    },
    {
      key: "Products",
      title: "Products",
      sortable: true,
      render: (data: RequestedStockInterface) => (
        <>
          <div>{data?.products?.length || "N/A"}</div>
        </>
      ),
    },
    {
      key: "Action",
      title: "Action",
      sortable: false,
      render: (data: RequestedStockInterface) => (
        <div className="flex justify-center">
          <button
            onClick={() => handleViewProducts(data.products)}
            className="text-blue-500 hover:text-blue-700 p-1"
            title="View Products"
          >
            <Eye className="w-5 h-5" />
          </button>
        </div>
      ),
    },
  ];
  return (
    <div>
      {isViewModalOpen && (
        <ViewRequestedProductsModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          products={selectedProducts}
        />
      )}
      {isRequestModalOpen && (
        <CreateRequestModal
          isOpen={isRequestModalOpen}
          onClose={() => setIsRequestModalOpen(false)}
          onSuccess={handleCreateRequest}
        />
      )}
      {clinicId && <div className="flex justify-end mx-2">
        <button
          onClick={() => setIsRequestModalOpen(true)}
          className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded"
        >
          Create Request
        </button>
      </div>}

      {requestedStock.length > 0 ? (
        <DataTable
          data={requestedStock}
          columns={columns}
          itemsPerPage={pagination.pageSize}
          isLoading={loading}
          pagination={{
            currentPage: pagination.page,
            totalPages: pagination.totalPages,
            totalItemsCount: pagination.totalItemsCount,
            pageSize: pagination.pageSize,
            setCurrentPage: (p) =>
              setPagination((prev) => ({ ...prev, page: p })),
            setTotalPages: (p) =>
              setPagination((prev) => ({ ...prev, totalPages: p })),
            setPageSize: (p) =>
              setPagination((prev) => ({ ...prev, pageSize: p })),
          }}
        />
      ) : (
        <p className="text-center text-gray-500 my-10 ">No data found</p>
      )}
    </div>
  );
};

export default RequestedStock;
